/*
 * Tiny engine that turns a plain-data `header` spec into the header parts the
 * detail page expects (eyebrow / title / status / ids / meta / stats / …).
 *
 * The goal: a module's header should read like a form you fill in, not code.
 * Each config just lists WHICH fields to show, their labels, icons and order –
 * this file does the boring work of reading the values and formatting them.
 *
 * A "field" is the API field key. Anywhere a field is expected you may pass:
 *   'fieldName'                    → that one field
 *   ['fieldA', 'fieldB']           → the first of these that has a value
 *
 * For the rare value that needs real maths (a salary range, years+months, …)
 * use `compute: ({ val, has }) => 'text'` and write the little formula there.
 */
import { asText, capitalize, statusColor, formatDateTime, has } from './configUtils';

// First field (from a key or list of keys) that actually has a value.
function pick(val, source) {
  const keys = Array.isArray(source) ? source : [source];
  for (const key of keys) if (has(val(key))) return val(key);
  return undefined;
}

// Join several fields together, skipping the empty ones (e.g. first + last).
function joinFields(val, keys, separator = ' ') {
  return keys.map((k) => val(k)).filter(has).map(asText).join(separator);
}

// Resolve one "line of text" item used by meta rows and the eyebrow.
// Honours: compute | join (fields) | field, plus an optional prefix/suffix.
function buildText(ctx, item) {
  const { val } = ctx;
  let text;
  if (typeof item.compute === 'function') text = item.compute(ctx);
  else if (item.join) text = joinFields(val, item.fields ?? [], item.join);
  else { const raw = pick(val, item.field); text = has(raw) ? asText(raw) : ''; }

  if (!has(text)) return '';
  return `${item.prefix ?? ''}${text}${item.suffix ?? ''}`;
}

// Normalise the eyebrow shorthand: a bare string/array means "show this field".
function normaliseItem(item) {
  if (typeof item === 'function') return { compute: item };
  if (typeof item === 'string' || Array.isArray(item)) return { field: item };
  return item;
}

export function buildFromSpec(spec = {}, ctx) {
  const { val } = ctx;
  const out = {};

  // ── Title — joins every listed field with a space (one field is fine too) ──
  if (spec.title !== undefined) {
    const keys = Array.isArray(spec.title) ? spec.title : [spec.title];
    out.title = joinFields(val, keys) || '-';
  }

  // ── Eyebrow — small line above the title ───────────────────────────────────
  if (spec.eyebrow !== undefined) {
    out.eyebrow = buildText(ctx, normaliseItem(spec.eyebrow)) || undefined;
  }

  // ── Client name (used by some modules next to the title) ───────────────────
  if (spec.clientName !== undefined) {
    out.clientName = buildText(ctx, normaliseItem(spec.clientName)) || undefined;
  }

  // ── Status pill ────────────────────────────────────────────────────────────
  if (spec.status !== undefined) {
    if (typeof spec.status === 'function') {
      out.status = spec.status(ctx) ?? null;
    } else {
      const raw = pick(val, spec.status.field);
      out.status = has(raw)
        ? { label: capitalize(raw), color: statusColor(raw) }
        : null;
    }
  }

  // ── Identifier chips (ids = under the title, titleIds = beside it) ─────────
  const buildChips = (chips = []) =>
    chips
      .map((chip) => {
        const value = typeof chip.compute === 'function'
          ? chip.compute(ctx)
          : asText(pick(val, chip.field) ?? '');
        return { label: chip.label, value };
      })
      .filter((chip) => has(chip.value));

  if (spec.ids !== undefined) out.ids = buildChips(spec.ids);
  if (spec.titleIds !== undefined) out.titleIds = buildChips(spec.titleIds);

  // ── Meta row (icon + text) ─────────────────────────────────────────────────
  if (spec.meta !== undefined) {
    out.meta = spec.meta
      .map((item) => ({ icon: item.icon, text: buildText(ctx, item) }))
      .filter((item) => has(item.text));
  }

  // ── Stats (right-hand summary numbers) ─────────────────────────────────────
  if (spec.stats !== undefined) {
    out.stats = spec.stats
      .map((stat) => {
        const raw = typeof stat.compute === 'function' ? stat.compute(ctx) : pick(val, stat.field);
        const value = has(raw) ? asText(raw) : (stat.always ? asText(stat.fallback ?? 0) : undefined);
        return { label: stat.label, value };
      })
      .filter((stat) => stat.value !== undefined);
  }

  // ── Timestamp line (a date, e.g. "Created Date : …") ───────────────────────
  if (spec.timestamp !== undefined) {
    const value = formatDateTime(pick(val, spec.timestamp.field));
    out.timestamp = has(value) ? { label: spec.timestamp.label, value } : null;
  }

  return out;
}

export default buildFromSpec;
